import ExcelJS from 'exceljs';

export const EXCEL_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet; charset=UTF-8';

export const INFO_COL_COUNT = 4;

export const GREEN_HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FF217346' },
};

export const UPDATE_TIME_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFFF2CC' },
};

export const ACTUAL_FORECAST_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFFC000' },
};

export const STUDIO_COLUMN_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFE8F5E9' },
};

export const YEAR_COLOR_PALETTE = [
  'FF7030A0',
  'FF0070C0',
  'FF375623',
  'FFB73A3A',
];

const createCellBorder = (): Partial<ExcelJS.Borders> => ({
  top: { style: 'thin', color: { argb: 'FF000000' } },
  left: { style: 'thin', color: { argb: 'FF000000' } },
  bottom: { style: 'thin', color: { argb: 'FF000000' } },
  right: { style: 'thin', color: { argb: 'FF000000' } },
});

const applyBorder = (cell: ExcelJS.Cell) => {
  cell.border = createCellBorder() as ExcelJS.Borders;
};

export const applyTableBorders = (
  sheet: ExcelJS.Worksheet,
  startRow: number,
  endRow: number,
  endCol: number,
) => {
  for (let rowNum = startRow; rowNum <= endRow; rowNum += 1) {
    const row = sheet.getRow(rowNum);
    for (let colNum = 1; colNum <= endCol; colNum += 1) {
      const cell = row.getCell(colNum);
      // Materialize empty cells so ExcelJS writes border styles to the file.
      if (cell.value === undefined) {
        cell.value = null;
      }
      applyBorder(cell);
    }
  }
};

export const styleDataCell = (
  cell: ExcelJS.Cell,
  align: 'left' | 'center' | 'right' = 'center',
  bold = false,
  colorArgb?: string,
) => {
  cell.font = {
    bold,
    size: 10,
    ...(colorArgb ? { color: { argb: colorArgb } } : {}),
  };
  cell.alignment = { vertical: 'middle', horizontal: align };
};

/**
 * Resolve highlight rule for an info-column cell by exact text match.
 */
export const resolveInfoCellStyle = (
  column: 1 | 2 | 3 | 4,
  text: string,
  highlights: Array<{
    column: 1 | 2 | 3 | 4;
    texts: string[];
    color?: string;
    bold?: boolean;
  }> = [],
): { bold: boolean; color?: string } => {
  const match = highlights.find(
    (rule) => rule.column === column && rule.texts.includes(text),
  );
  return { bold: !!match?.bold, color: match?.color };
};

export const styleGreenHeaderCell = (
  cell: ExcelJS.Cell,
  align: 'left' | 'center' = 'center',
) => {
  cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
  cell.alignment = { vertical: 'middle', horizontal: align, wrapText: true };
  cell.fill = GREEN_HEADER_FILL;
};

export const styleYearHeaderCell = (cell: ExcelJS.Cell, yearIndex: number) => {
  const argb = YEAR_COLOR_PALETTE[yearIndex % YEAR_COLOR_PALETTE.length];
  cell.font = { color: { argb: 'FFFFFFFF' }, size: 10 };
  cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
};

export const styleActualForecastCell = (
  cell: ExcelJS.Cell,
  // null means the API did not provide Actual/Forecast — keep the cell empty.
  isActual: boolean | null,
) => {
  cell.font = { color: { argb: 'FF000000' }, size: 10 };
  cell.alignment = { horizontal: 'center', vertical: 'middle' };
  if (isActual === null) {
    cell.value = '';
    return;
  }
  cell.value = isActual ? 'Actual' : 'Forecast';
  if (isActual) {
    cell.fill = ACTUAL_FORECAST_FILL;
  }
};
